import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BaseWorkflowEditor from './BaseWorkflowEditor';
import NotificationModal from '../shared/NotificationModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface BaseWorkflow {
  name: string;
  path: string;
  sha: string;
  size: number;
  download_url: string;
}

const BaseWorkflowsList: React.FC = () => {
  const [workflows, setWorkflows] = useState<BaseWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deletePassword, setDeletePassword] = useState('');

  // Notification modal state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'error' | 'info'>('info');

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/base-workflows`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkflows(response.data);
    } catch (error: any) {
      console.error('Failed to fetch base workflows:', error);
      setNotificationMessage(error.response?.data?.error || 'Failed to fetch base workflows');
      setNotificationType('error');
      setShowNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (filename: string) => {
    setDeleteTarget(filename);
    setDeletePassword('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    if (!deletePassword.trim()) {
      setNotificationMessage('Please enter the delete confirmation password');
      setNotificationType('error');
      setShowNotification(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/base-workflows/${deleteTarget}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { confirmPassword: deletePassword }
      });
      setShowDeleteModal(false);
      setDeleteTarget(null);
      setDeletePassword('');
      setNotificationMessage('Base workflow deleted successfully!');
      setNotificationType('success');
      setShowNotification(true);
      fetchWorkflows();
    } catch (error: any) {
      console.error('Failed to delete workflow:', error);
      setNotificationMessage(error.response?.data?.error || 'Failed to delete workflow');
      setNotificationType('error');
      setShowNotification(true);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setDeletePassword('');
  };

  const handleCreateNew = () => {
    setSelectedWorkflow(null);
    setShowEditor(true);
  };

  const handleEdit = (filename: string) => {
    setSelectedWorkflow(filename);
    setShowEditor(true);
  };

  const handleEditorClose = (saved: boolean) => {
    setShowEditor(false);
    setSelectedWorkflow(null);
    if (saved) {
      fetchWorkflows();
    }
  };

  const filteredWorkflows = workflows.filter(w =>
    w.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading base workflows...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Base Workflows</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage workflow JSON files in{' '}
            <a
              href="https://github.com/HYGO-INC/base-workflows"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              github.com/HYGO-INC/base-workflows
            </a>
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          <span className="text-lg">+</span>
          Create New Workflow
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search workflows..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Workflows List */}
      {filteredWorkflows.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {searchQuery ? 'No workflows match your search.' : 'No base workflows found. Create your first one!'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredWorkflows.map((workflow) => (
            <div
              key={workflow.sha}
              className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📄</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{workflow.name}</h3>
                      <p className="text-sm text-gray-500">
                        Size: {(workflow.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(workflow.name)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(workflow.name)}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && (
        <BaseWorkflowEditor
          filename={selectedWorkflow}
          onClose={handleEditorClose}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Delete</h3>
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                You are about to delete{' '}
                <span className="font-semibold text-red-600">{deleteTarget}</span>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                This will trigger a GitHub commit and deployment. This action cannot be undone.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter delete confirmation password:
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && confirmDelete()}
                placeholder="Enter password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                autoComplete="new-password"
                autoFocus
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotification}
        onClose={() => setShowNotification(false)}
        message={notificationMessage}
        type={notificationType}
      />
    </div>
  );
};

export default BaseWorkflowsList;
